/**
 * PECR-compliant cookie consent banner.
 *
 * Shown on every page when no consent decision has been stored. The banner
 * stays fixed to the bottom of the viewport. PostHog is NOT initialised
 * until the user explicitly accepts — the parent is responsible for calling
 * initPostHog() in the onAccept callback.
 */
import { useState } from 'react';
import { T, RADIUS, SHADOW } from '@/lib/tokens';

interface CookieConsentBannerProps {
  onAccept: () => void;
  onDecline: () => void;
}

/**
 *
 * @param root0
 * @param root0.onAccept
 * @param root0.onDecline
 */
export function CookieConsentBanner({ onAccept, onDecline }: CookieConsentBannerProps) {
  const [declineHovered, setDeclineHovered] = useState(false);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center px-4 pb-4">
      <div
        className="w-full max-w-[420px] px-5 py-4"
        role="dialog"
        aria-label="Cookie consent"
        aria-modal="false"
        style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: `${RADIUS.xl}px ${RADIUS.xl}px 0 0`,
          boxShadow: SHADOW.lg,
        }}
      >
        <p className="mb-1 text-sm font-semibold leading-snug" style={{ color: T.text }}>
          Analytics cookies
        </p>
        <p
          className="mb-4 text-xs leading-relaxed"
          style={{ color: T.textSec, lineHeight: '1.65' }}
        >
          We use analytics cookies (PostHog) to understand how ShareShack is used and improve the
          experience. No personal data is shared with third parties. You can change your preference
          at any time from your account settings.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onAccept}
            className="flex-1 px-5 py-2.5 text-sm font-semibold leading-none text-white transition-opacity hover:opacity-90"
            style={{
              background: T.accent,
              borderRadius: RADIUS.md,
            }}
          >
            Accept
          </button>
          <button
            onClick={onDecline}
            onMouseEnter={() => setDeclineHovered(true)}
            onMouseLeave={() => setDeclineHovered(false)}
            className="flex-1 px-5 py-2.5 text-sm font-semibold leading-none transition-colors"
            style={{
              color: T.textSec,
              border: `1px solid ${declineHovered ? T.borderStrong : T.border}`,
              borderRadius: RADIUS.md,
              background: declineHovered ? T.surfaceSubtle : 'transparent',
            }}
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}
